import React from "react";
import { SelectWithSpinAndCustomMaxTag } from "@src/components/select/SelectWithCustomMaxTag";
import { Radio } from "antd";
import { generateCountries } from "@src/util/country-utils";

type CountryMode = "is" | "is not";

type CountryInputValue = {
    mode: CountryMode;
    countries: string[];
};

type CountryInputProps = {
    onChange?: (value: CountryInputValue) => void;
    value?: CountryInputValue;
    placeholder?: string;
    style?: React.CSSProperties;
};

const COUNTRIES = generateCountries();

export const CountryInput: React.FC<CountryInputProps> = ({
    onChange,
    value,
    placeholder = "Select countries",
    style = { width: 300 },
}) => {
    const [currentMode, setCurrentMode] = React.useState<CountryMode>(value?.mode || "is");
    const [selectedCountries, setSelectedCountries] = React.useState<string[]>(value?.countries || []);

    const countryOptions = React.useMemo(() => {
        return COUNTRIES.map((country) => ({
            label: `${country.name} (${country.code})`,
            value: country.code,
            title: country.name,
        }));
    }, []);

    const handleModeChange = (e: any) => {
        const newMode = e.target.value as CountryMode;
        setCurrentMode(newMode);
        onChange?.({
            mode: newMode,
            countries: selectedCountries, // Keep the existing selected countries
        });
    };

    const handleCountriesChange = (countries: string[]) => {
        setSelectedCountries(countries);
        onChange?.({
            mode: currentMode,
            countries,
        });
    };

    // Update state when value prop changes
    React.useEffect(() => {
        if (value) {
            setCurrentMode(value.mode);
            setSelectedCountries(value.countries);
        }
    }, [value]);

    return (
        <div style={style} className="flex items-center gap-2">
            <Radio.Group value={currentMode} onChange={handleModeChange} size="small">
                <Radio.Button value="is">Is</Radio.Button>
                <Radio.Button value="is not">Is not</Radio.Button>
            </Radio.Group>

            <SelectWithSpinAndCustomMaxTag
                className="flex-1"
                options={countryOptions}
                placeholder={placeholder}
                showSearch
                mode="multiple"
                value={selectedCountries}
                onChange={handleCountriesChange}
                filterOption={(input, option) => {
                    const { title, value, label } = option as any;
                    if (!value || !title || !label) return false;
                    const searchInput = input.toLowerCase();
                    return (
                        title.toString().toLowerCase().indexOf(searchInput) >= 0 ||
                        value.toString().toLowerCase().indexOf(searchInput) >= 0 ||
                        label.toString().toLowerCase().indexOf(searchInput) >= 0
                    );
                }}
                allowClear
            />
        </div>
    );
};
